import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  IncidentInvestigationSessionDto,
  IncidentInvestigationStepDto,
  IncidentInvestigationMessageDto,
  IncidentCorrelationDto,
  InvestigationTemplateDto,
  InvestigationFilters,
  CreateInvestigationRequest,
  UpdateInvestigationRequest,
  CompleteInvestigationRequest,
  AddInvestigationStepRequest,
  UpdateInvestigationStepRequest,
  AddCorrelationRequest,
  CreateInvestigationTemplateRequest,
  UpdateInvestigationTemplateRequest,
} from '../types/investigation';

/**
 * Investigation service for managing incident investigations (Module 24).
 */
export class InvestigationService extends BaseService {
  /**
   * Gets a paginated list of investigation sessions with optional filtering.
   */
  async getInvestigations(filters?: InvestigationFilters): Promise<PagedResult<IncidentInvestigationSessionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.investigationStatus !== undefined)
      queryParams.append('investigationStatus', String(filters.investigationStatus));
    if (filters?.severity !== undefined) queryParams.append('severity', String(filters.severity));
    if (filters?.relatedIncidentId) queryParams.append('relatedIncidentId', filters.relatedIncidentId);
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const url = `/investigations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<IncidentInvestigationSessionDto>>(url);
  }

  /**
   * Gets an investigation session by ID.
   */
  async getInvestigation(id: string): Promise<IncidentInvestigationSessionDto> {
    return this.get<IncidentInvestigationSessionDto>(`/investigations/${id}`);
  }

  /**
   * Creates a new investigation session.
   */
  async createInvestigation(request: CreateInvestigationRequest): Promise<IncidentInvestigationSessionDto> {
    return this.post<IncidentInvestigationSessionDto>('/investigations', request);
  }

  /**
   * Updates an investigation session.
   */
  async updateInvestigation(id: string, request: UpdateInvestigationRequest): Promise<IncidentInvestigationSessionDto> {
    return this.put<IncidentInvestigationSessionDto>(`/investigations/${id}`, request);
  }

  /**
   * Completes an investigation session.
   */
  async completeInvestigation(id: string, request?: CompleteInvestigationRequest): Promise<IncidentInvestigationSessionDto> {
    return this.post<IncidentInvestigationSessionDto>(`/investigations/${id}/complete`, request);
  }

  /**
   * Gets investigation steps for an investigation session.
   */
  async getInvestigationSteps(id: string): Promise<IncidentInvestigationStepDto[]> {
    return this.get<IncidentInvestigationStepDto[]>(`/investigations/${id}/steps`);
  }

  /**
   * Adds a new investigation step to an investigation session.
   */
  async addInvestigationStep(id: string, request: AddInvestigationStepRequest): Promise<IncidentInvestigationStepDto> {
    return this.post<IncidentInvestigationStepDto>(`/investigations/${id}/steps`, request);
  }

  /**
   * Updates an investigation step.
   */
  async updateInvestigationStep(
    id: string,
    stepId: string,
    request: UpdateInvestigationStepRequest
  ): Promise<IncidentInvestigationStepDto> {
    return this.put<IncidentInvestigationStepDto>(`/investigations/${id}/steps/${stepId}`, request);
  }

  /**
   * Gets investigation messages for an investigation session.
   */
  async getInvestigationMessages(id: string): Promise<IncidentInvestigationMessageDto[]> {
    return this.get<IncidentInvestigationMessageDto[]>(`/investigations/${id}/messages`);
  }

  /**
   * Gets investigation correlations for an investigation session.
   */
  async getInvestigationCorrelations(id: string): Promise<IncidentCorrelationDto[]> {
    return this.get<IncidentCorrelationDto[]>(`/investigations/${id}/correlations`);
  }

  /**
   * Adds a correlation to an investigation session.
   */
  async addCorrelation(id: string, request: AddCorrelationRequest): Promise<IncidentCorrelationDto> {
    return this.post<IncidentCorrelationDto>(`/investigations/${id}/correlations`, request);
  }

  /**
   * Gets investigation templates.
   */
  async getInvestigationTemplates(activeOnly = true): Promise<InvestigationTemplateDto[]> {
    const url = `/investigations/templates?activeOnly=${encodeURIComponent(String(activeOnly))}`;
    return this.get<InvestigationTemplateDto[]>(url);
  }

  /**
   * Creates a new investigation template.
   */
  async createInvestigationTemplate(
    request: CreateInvestigationTemplateRequest
  ): Promise<InvestigationTemplateDto> {
    return this.post<InvestigationTemplateDto>('/investigations/templates', request);
  }

  /**
   * Updates an investigation template.
   */
  async updateInvestigationTemplate(
    id: string,
    request: UpdateInvestigationTemplateRequest
  ): Promise<InvestigationTemplateDto> {
    return this.put<InvestigationTemplateDto>(`/investigations/templates/${id}`, request);
  }

  /**
   * Deletes an investigation template.
   */
  async deleteInvestigationTemplate(id: string): Promise<boolean> {
    return this.delete<boolean>(`/investigations/templates/${id}`);
  }
}

export const investigationService = new InvestigationService();

